import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';

import { escapeHtml } from '../lib/cells';

/**
 * 자유 텍스트 신규 생성 옵션이 선택됐을 때 onSelect 로 전달되는 표시 객체.
 *
 * 호출 측은 typeof item === 'object' && '__new' in item 으로 분기.
 */
export interface FreeTextItem {
  __new: true;
  query: string;
}

export interface AutocompleteInputOptions<T> {
  /** input placeholder */
  placeholder?: string;
  /** 초기값 — 폼 prefill 또는 편집 모드 진입 시 */
  initialValue?: string;
  /** 추가 CSS 클래스 (래퍼에 적용) — 예: 'autocomplete-input-wide' */
  className?: string;
  /** 검색 함수 — abort signal 전달됨 (이전 요청 취소용) */
  fetchSuggestions: (query: string, signal: AbortSignal) => Promise<T[]>;
  /** 한 row 의 표시 HTML (이미 escape 된 안전한 문자열 반환) */
  renderItem: (item: T) => string;
  /** 사용자가 row 선택 또는 "신규 생성" 선택 시 호출 */
  onSelect: (item: T | FreeTextItem) => void;
  /** 매칭 없을 때 "신규 생성" 옵션 표시 여부 (default false) */
  allowFreeText?: boolean;
  /** 신규 옵션 라벨 — 예: q => `＋ "${q}" 신규 customer 로 등록` */
  freeTextLabel?: (query: string) => string;
  /** 디바운스 ms (default 280) */
  debounceMs?: number;
  /** listbox 한 번에 표시할 max 개수 — fetchSuggestions 가 이미 limit 처리하면 무의미 (default 10) */
  maxItems?: number;
}

interface AutocompleteData {
  query: string;
  open: boolean;
  loading: boolean;
  highlightIdx: number;
  hasResults: boolean;
}

/**
 * 🎯 AutocompleteInput<T>
 *
 * 재사용 가능한 자동완성 input. customer 검색, option_part 검색 등에 사용.
 *
 * 사용 예:
 *   const ac = new AutocompleteInput<CustomerRow>({
 *     placeholder: '고객사명',
 *     fetchSuggestions: (q, signal) => api.searchCustomers(q, 10, signal),
 *     renderItem: c => `${c.name} <span class="ac-dim">(${c.business_no})</span>`,
 *     allowFreeText: true,
 *     freeTextLabel: q => `＋ "${q}" 신규 customer 로 등록`,
 *     onSelect: item => { ... },
 *   });
 *
 * 키보드:
 *   ↓↑      listbox 항목 이동
 *   Enter   현재 항목 선택
 *   Esc     listbox 닫기 (input 값 유지)
 *   Tab     listbox 닫고 다음 form 요소로 (값 유지)
 *
 * blur 시 150ms 후 listbox 닫음 (클릭 처리 시간 확보).
 */
export class AutocompleteInput<T> extends SolaView<AutocompleteData> {
  private readonly opts: Required<
    Pick<AutocompleteInputOptions<T>, 'debounceMs' | 'maxItems'>
  > &
    AutocompleteInputOptions<T>;

  /** 현재 listbox 에 표시 중인 아이템들 (highlight idx 매핑용) */
  private currentItems: T[] = [];

  private debounceTimer?: ReturnType<typeof setTimeout>;
  private blurTimer?: ReturnType<typeof setTimeout>;
  private abortController?: AbortController;

  override events = {
    input: { '@query': 'handleInput' },
    keydown: { '@query': 'handleKeydown' },
    // focus/blur 는 bubble 안 함 → focusin/focusout 사용 (Sola root 위임 가능)
    focusin: { '@query': 'handleFocus' },
    focusout: { '@query': 'handleBlur' },
    mousedown: {
      '@item': 'handleItemMouseDown',
      '@free-text': 'handleFreeTextMouseDown',
    },
    mouseover: { '@item': 'handleItemHover' },
  };

  constructor(opts: AutocompleteInputOptions<T>) {
    super();
    this.opts = {
      debounceMs: 280,
      maxItems: 10,
      ...opts,
    };
    this.data = {
      query: opts.initialValue ?? '',
      open: false,
      loading: false,
      highlightIdx: -1,
      hasResults: false,
    };
  }

  override onUnmount(): void {
    super.onUnmount();
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (this.blurTimer) clearTimeout(this.blurTimer);
    this.abortController?.abort();
  }

  render(): HTMLElement {
    const cls = this.opts.className ? ` ${this.opts.className}` : '';
    return htmlSolaWithInstance(this)`
      <div class="autocomplete-wrap${cls}">
        <input
          type="text"
          class="autocomplete-input"
          data-action="query"
          autocomplete="off"
          spellcheck="false"
          placeholder="${escapeHtml(this.opts.placeholder ?? '')}"
          value="${escapeHtml(this.data.query)}"
        />
        <div class="autocomplete-listbox hidden" role="listbox"></div>
      </div>
    `;
  }

  // ===== 외부 API =====

  /** input 값을 외부에서 강제 설정 (예: 선택 후 다른 row 채울 때). */
  public setValue(value: string): void {
    this.data.query = value;
    const input = this._rootElement?.querySelector(
      '.autocomplete-input',
    ) as HTMLInputElement | null;
    if (input) input.value = value;
    this.closeListbox();
  }

  /** input 에 포커스. */
  public focus(): void {
    const input = this._rootElement?.querySelector(
      '.autocomplete-input',
    ) as HTMLInputElement | null;
    input?.focus();
  }

  // ===== 이벤트 핸들러 =====

  handleInput(_e: Event, target: HTMLElement): void {
    const q = (target as HTMLInputElement).value;
    this.data.query = q;

    if (this.debounceTimer) clearTimeout(this.debounceTimer);

    if (q.trim().length === 0) {
      this.abortController?.abort();
      this.currentItems = [];
      this.data.hasResults = false;
      this.data.loading = false;
      this.closeListbox();
      return;
    }

    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = undefined;
      void this.runFetch(q);
    }, this.opts.debounceMs);
  }

  handleKeydown(e: Event): void {
    const ev = e as KeyboardEvent;
    const total = this.currentItems.length;
    const hasFreeText = this.shouldShowFreeText();
    const totalNavigable = total + (hasFreeText ? 1 : 0);

    switch (ev.key) {
      case 'ArrowDown':
        ev.preventDefault();
        if (!this.data.open) {
          if (this.data.query.trim()) void this.runFetch(this.data.query);
          return;
        }
        if (totalNavigable === 0) return;
        this.data.highlightIdx = Math.min(
          this.data.highlightIdx + 1,
          totalNavigable - 1,
        );
        this.refreshHighlight();
        break;

      case 'ArrowUp':
        ev.preventDefault();
        if (!this.data.open) return;
        this.data.highlightIdx = Math.max(this.data.highlightIdx - 1, -1);
        this.refreshHighlight();
        break;

      case 'Enter':
        if (!this.data.open || this.data.highlightIdx < 0) return;
        ev.preventDefault();
        this.commitHighlighted();
        break;

      case 'Escape':
        if (this.data.open) {
          ev.preventDefault();
          this.closeListbox();
        }
        break;

      case 'Tab':
        // listbox 닫고 다음 요소로 (현재 입력값 그대로 유지)
        this.closeListbox();
        break;

      default:
        // 다른 키는 input 이벤트가 받아 처리
        break;
    }
  }

  handleFocus(): void {
    // 포커스 복귀 시, 결과 있고 닫혀있으면 다시 열기
    if (this.currentItems.length > 0 && !this.data.open) {
      this.data.open = true;
      this.refreshListbox();
    }
  }

  handleBlur(): void {
    // 150ms 후 닫기 — listbox 클릭 처리 시간 확보
    if (this.blurTimer) clearTimeout(this.blurTimer);
    this.blurTimer = setTimeout(() => {
      this.blurTimer = undefined;
      this.closeListbox();
    }, 150);
  }

  /** mousedown 으로 처리하면 input blur 이전에 onSelect 호출 가능 (timing 문제 회피). */
  handleItemMouseDown(e: Event, target: HTMLElement): void {
    e.preventDefault(); // input blur 방지
    const idx = Number(target.dataset.idx);
    if (!Number.isInteger(idx)) return;
    this.data.highlightIdx = idx;
    this.commitHighlighted();
  }

  handleFreeTextMouseDown(e: Event): void {
    e.preventDefault();
    this.commitFreeText();
  }

  handleItemHover(_e: Event, target: HTMLElement): void {
    const idx = Number(target.dataset.idx);
    if (!Number.isInteger(idx)) return;
    if (idx !== this.data.highlightIdx) {
      this.data.highlightIdx = idx;
      this.refreshHighlight();
    }
  }

  // ===== 내부 로직 =====

  private async runFetch(q: string): Promise<void> {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.data.loading = true;
    this.openListbox();
    this.refreshListbox();

    try {
      const items = await this.opts.fetchSuggestions(
        q,
        this.abortController.signal,
      );
      // 입력이 그 사이 또 바뀌었으면 (debounce 후 새 fetch) 폐기
      if (q !== this.data.query) return;
      this.currentItems = items.slice(0, this.opts.maxItems);
      this.data.hasResults = this.currentItems.length > 0;
      this.data.highlightIdx = this.currentItems.length > 0 ? 0 : -1;
      this.data.loading = false;
      this.refreshListbox();
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      // 조용히 실패 — 사용자한테 검색 에러 띄우면 흐름 방해
      console.error('[AutocompleteInput] fetch failed:', err);
      this.currentItems = [];
      this.data.hasResults = false;
      this.data.loading = false;
      this.refreshListbox();
    }
  }

  private shouldShowFreeText(): boolean {
    if (!this.opts.allowFreeText) return false;
    if (!this.opts.freeTextLabel) return false;
    return this.data.query.trim().length > 0;
  }

  private commitHighlighted(): void {
    const total = this.currentItems.length;
    const idx = this.data.highlightIdx;
    if (idx < 0) return;

    if (idx < total) {
      const item = this.currentItems[idx];
      this.closeListbox();
      this.opts.onSelect(item);
    } else if (this.shouldShowFreeText()) {
      // 마지막 항목이 free-text
      this.commitFreeText();
    }
  }

  private commitFreeText(): void {
    const query = this.data.query.trim();
    if (!query) return;
    this.closeListbox();
    this.opts.onSelect({ __new: true, query } as FreeTextItem);
  }

  private openListbox(): void {
    this.data.open = true;
  }

  private closeListbox(): void {
    this.data.open = false;
    const box = this._rootElement?.querySelector('.autocomplete-listbox');
    if (box) box.classList.add('hidden');
  }

  // ===== 렌더링 =====

  private refreshListbox(): void {
    const box = this._rootElement?.querySelector('.autocomplete-listbox');
    if (!box) return;

    if (!this.data.open) {
      box.classList.add('hidden');
      return;
    }

    const parts: string[] = [];

    if (this.data.loading) {
      parts.push('<div class="autocomplete-status">검색 중…</div>');
    } else if (this.currentItems.length === 0 && !this.shouldShowFreeText()) {
      parts.push('<div class="autocomplete-status">일치하는 결과가 없습니다.</div>');
    } else {
      // 아이템들
      this.currentItems.forEach((item, idx) => {
        const active = idx === this.data.highlightIdx ? ' active' : '';
        parts.push(
          `<div class="autocomplete-item${active}" data-action="item" data-idx="${idx}" role="option">${this.opts.renderItem(item)}</div>`,
        );
      });

      // free-text 옵션 (있다면 맨 아래 + 구분선)
      if (this.shouldShowFreeText()) {
        const freeIdx = this.currentItems.length;
        const active = freeIdx === this.data.highlightIdx ? ' active' : '';
        const label = this.opts.freeTextLabel!(this.data.query.trim());
        if (this.currentItems.length > 0) {
          parts.push('<div class="autocomplete-separator"></div>');
        }
        parts.push(
          `<div class="autocomplete-item autocomplete-free-text${active}" data-action="free-text" role="option">${escapeHtml(label)}</div>`,
        );
      }
    }

    box.innerHTML = parts.join('');
    box.classList.remove('hidden');
  }

  /** 하이라이트만 갱신 — 전체 redraw 안 함 (키 네비 빠르게) */
  private refreshHighlight(): void {
    const box = this._rootElement?.querySelector('.autocomplete-listbox');
    if (!box) return;
    box.querySelectorAll('.autocomplete-item').forEach((el, idx) => {
      el.classList.toggle('active', idx === this.data.highlightIdx);
    });
    // 활성 항목 scrollIntoView
    const activeEl = box.querySelector('.autocomplete-item.active');
    if (activeEl) {
      (activeEl as HTMLElement).scrollIntoView({ block: 'nearest' });
    }
  }
}
